import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * マウスホバー状態を管理するカスタムフック
 *
 * 要素のホバー状態を追跡し、その状態に基づいてUIを制御するために使用します。
 * 主にホバーメニュー、条件付きUIの表示などに利用されることを想定してます。
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { ref, isHovered } = useHover<HTMLDivElement>();
 *
 *   return (
 *     <div ref={ref}>
 *       {isHovered && <HoverContent />}
 *     </div>
 *   );
 * };
 * ```
 */
export function useHover<T extends HTMLElement>() {
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef<T | null>(null);
  const ref = useCallback((node: T | null) => {
    nodeRef.current = node;
  }, []);
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    node.addEventListener('mouseenter', handleMouseEnter);
    node.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      node.removeEventListener('mouseenter', handleMouseEnter);
      node.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);
  return {
    ref,
    isHovered
  };
}